import { defineStore } from 'pinia';
import { computed } from 'vue';
import { useBrowserLocation, type BrowserLocationState } from '@vueuse/core';
import { buildUrl } from '~/utils/commonUtils';
import { isSSR } from '~/utils/isSSR';
import { REDIRECT_MAP } from '../constants/common';

type RedirectMapKeys = keyof typeof REDIRECT_MAP;

export const useUrlLocationOperationsStore = defineStore('urlLocationOperations', () => {
  // useBrowserLocation returns a reactive object linked to window.location
  // In SSR, it returns an empty object.
  const browserLocation = useBrowserLocation();

  /**
   * Reactive location object that works in both SSR and CSR
   */
  const location = computed<Partial<BrowserLocationState>>(() => (isSSR() ? {} : browserLocation.value));

  /**
   * constructs base url, based on the path mapping
   */
  const getBaseUrl = (path: RedirectMapKeys): string => REDIRECT_MAP[path] ?? '';

  /** **** GETTERS ***** */

  /**
   * @example: ?foo=bar -> {object}
   */
  const getSearchParamsData = () => new URLSearchParams(location.value?.search ?? '');

  /**
   * @example http://example.com/index.php?foo=bar -> {object}
   */
  const getUrlData = () => {
    try {
      return new URL(location.value?.href ?? '');
    } catch {
      // Return a dummy URL or current request URL if href is invalid/missing
      return new URL('about:blank');
    }
  };

  /** **** ACTIONS ***** */

  /**
   * redirects to specified url based on type selection and in addition to query parameters
   */
  const redirectUrl = (type: RedirectMapKeys, queryParams = {}) => {
    const baseUrl = getBaseUrl(type);
    if (!baseUrl) return;

    const url = buildUrl(baseUrl, queryParams);

    if (!isSSR()) {
      // Directly update browser location
      window.location.href = url;
    } else {
      // Use Nuxt's navigateTo for server-side redirects
      navigateTo(url);
    }
  };

  return {
    location,
    getSearchParamsData,
    getUrlData,
    redirectUrl,
  };
});

export default useUrlLocationOperationsStore;
